import Promise from 'bluebird';
import _ from 'underscore';
import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { Overlay } from '@bingads-webui-react/fabric-control';
import PropTypes from 'prop-types';
import { stringifySync } from '@bingads-webui/safe-json';
import classnames from 'classnames';
import { DEFAULT_PAGE_SIZE, AdsStudioPageName, AdsStudioSpinner, FolderAssetType } from '@bingads-webui-campaign-react/ads-studio-app-shared-components';
import { usePageActiveTimeTracker } from '@bingads-webui-campaign-react/common-hooks';

import { useFilterBar } from './hooks/use-filter-bar';
import { useLoadData } from './hooks/use-load-data';
import { useTableColumns } from './hooks/use-table-columns';
import { usePerformancePage } from './hooks/use-performance-page';
import { usePerformancePageStyles, usePerformancePageStaticStyles } from './styles/performance-styles';
import { useInlineChartContainerStyle } from './styles/chart-styles';

/* istanbul ignore next */
export const PerformancePageComponent = (props) => {
  const {
    renderHeaderWithPopup, navigation, notificationApi, reload, renderToolbarContainer, renderDetailsList, renderInlineChart, dataService, getChartPreference, perfMetricDateRange, adsStudioUserPreferences, renderDatePicker, i18n, logger, dateFormat, ianaTimeZone, isAdsStudioAIGCMVPEnabled,
    useInlineDownload, createFolderFilterQuery, pagePerfMarker: performancePagePerfMarker, isAIGCRegionRestricted, setDataLoaded,
  } = props;
  usePerformancePageStaticStyles();
  const classes = usePerformancePageStyles();
  const chartClasses = useInlineChartContainerStyle();
  const [isChartShown, setChartShown] = useState(null);
  const [isPageInited, setPageInited] = useState(false);

  const {
    assets = [],
    totalAssetCount = 0,
    loadDataInDateRange,
    loadDataArgs,
    perfTimeSeriesData,
    loadPerfTimeSeriesData,
    loadMoreDataInDateRange,
    currentFolderId,
    dataOwnerPageName,
    renameAsset,
  } = dataService || {};

  const onFailedToLoadData = useCallback(() => {
    notificationApi.showWarning(i18n.getString(_TL_('Failed to load assets. Please refresh the page and try again.')));
  }, [i18n, notificationApi]);

  const onFilterValueChange = useCallback((curFilterState, newValue, newHumanizedValue) => {
    logger.logTrace('onPerfPageFilterValueChange', { ...(curFilterState || {}), newValue, newHumanizedValue });
    // handleRealUsedFlag(assetLibraryAccountPreferences, RealUsedFlagSuffix.App);
  }, [logger]);

  const {
    filterStates, filters, isEditingFilter, renderFilterBar, overwriteFilters,
  } = useFilterBar({
    onFilterValueChange, currentFolderId, dateFormat, ianaTimeZone, i18n, isAdsStudioAIGCMVPEnabled: isAdsStudioAIGCMVPEnabled && !isAIGCRegionRestricted,
  });

  const {
    isDownloadShown,
    setDownloadShown,
    renderInlineDownload,
  } = useInlineDownload({ overwriteFilters });

  const {
    loadingData,
    setLoadingData,
    loadingMoreData,
    loadNextPage,
    loadDataAsync,
  } = useLoadData({
    filters, perfMetricDateRange, onFailedToLoadData, loadMoreDataInDateRange, loadDataInDateRange, perfMarker: performancePagePerfMarker,
  });

  const onGotoDetailPage = useCallback((assetId) => {
    if (navigation) {
      navigation.gotoAssetDetails({
        assetId, assets, totalAssetCount, folderAssetType: FolderAssetType.Image, loadMoreData: loadNextPage, renameAsset,
      });
    }
  }, [assets, loadNextPage, navigation, renameAsset, totalAssetCount]);

  const onGotoEditPage = useCallback((assetId) => {
    if (navigation) {
      if (logger && logger.logTrace) {
        logger.logTrace('Image Details: edit image', { assetId });
      }
      navigation.gotoEditImagePage({ imageId: assetId });
    }
  }, [logger, navigation]);

  const {
    columns, sortByColumn, setSortByColumn, sortBy, expandColumns, getInlineDownloadColumns,
  } = useTableColumns({
    i18n, dateRange: perfMetricDateRange, onGotoDetailPage, onGotoEditPage, navigation, renderHeaderWithPopup,
  });

  const {
    pageSize,
    pager,
    pagedItems,
    setPageNumber,
    setPageSize,
  } = usePerformancePage({
    assets, loadNextPage, adsStudioUserPreferences, totalAssetCount, highlightedText: filters.searchText,
  });

  const hasNoData = _.isEmpty(assets);
  const shouldShowChart = useMemo(() => isDownloadShown === false && isChartShown !== null, [isChartShown, isDownloadShown]);
  const isPageLoading = useMemo(() => !isPageInited || loadingData, [isPageInited, loadingData]);
  const toolbar = useMemo(() => {
    const filterBar = renderFilterBar(filterStates, isEditingFilter);
    return !isDownloadShown && renderToolbarContainer({
      i18n, filterBar, isChartShown, setChartShown, isEditingFilter, setDownloadShown,
    });
  }, [filterStates, i18n, isChartShown, isDownloadShown, isEditingFilter, renderFilterBar, renderToolbarContainer, setDownloadShown]);

  const onSort = useCallback((newSortByColumn) => {
    setPageNumber(1);
    setSortByColumn(newSortByColumn);
    adsStudioUserPreferences.setPerfTableSortBy(newSortByColumn);
  }, [adsStudioUserPreferences, setPageNumber, setSortByColumn]);

  const getInlineDownloadFilter = useCallback(() => {
    // ensure this callback is readonly for the enough dependency check
    const serializedFilter = stringifySync(filterStates);
    const { isGlobalSearch, ...otherFilters } = filters;
    const args = loadDataArgs({
      ...otherFilters,
      folderId: currentFolderId,
    });
    if (isGlobalSearch) {
      // remove folderId for global search
      delete args.folderId;
    } else if (!args.folderId) {
      // use -1 for root folder
      args.folderId = -1;
    }
    const filterUrlParam = createFolderFilterQuery({ ...args.filterArgs, folderId: args.folderId, hasPerfData: true });
    return {
      filterUrlParam,
      serializedFilter,
    };
  // todo: add loadDataArgs dependency will cause inline download view be re-rendered when date-picker changes, user input will be lost
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStates, filters, currentFolderId]);

  // init page
  useEffect(() => {
    // restore the preference values
    if (!isPageInited && adsStudioUserPreferences) {
      const { perfTablePageSize, perfTableSortBy } = adsStudioUserPreferences;
      if (perfTablePageSize) {
        setPageSize(perfTablePageSize || DEFAULT_PAGE_SIZE);
      }
      if (perfTableSortBy) {
        onSort(perfTableSortBy);
      }
      setPageInited(true);
    }
  }, [adsStudioUserPreferences, isPageInited, onSort, setPageSize]);

  // load data for initial page load, or
  useEffect(() => {
    if (!isPageInited || loadingData) {
      // wait until page is initialized
      return;
    }

    if (reload) { // Important! Add a trivial log so that reload will be an explicit dep.
      logger.logTrace('force to reload');
    }

    performancePagePerfMarker.willFetchData();

    setLoadingData(true);
    loadDataAsync(pageSize, sortBy)
      .tap((ret) => {
        const { folder } = ret || {};
        const { itemCount } = folder || {};
        const { ImageAssetsCount } = itemCount || {};
        logger.logTrace('Performance page data loaded, ', { totalRowCount: ImageAssetsCount || 0 });
      })
      .tapCatch(() => { // throw out so that this error will be shown in console for easy debugging
        onFailedToLoadData();
      }).finally(() => {
        setLoadingData(false);

        performancePagePerfMarker.willRender();
        if (setDataLoaded) {
          setDataLoaded();
        }
        // todo: update inline chart if time series data is also included in the response
      });
  }, [i18n, isPageInited, loadDataAsync, logger, notificationApi, onFailedToLoadData, pageSize, reload, setLoadingData, sortBy]);

  usePageActiveTimeTracker(logger, 'Performance');

  useEffect(() => {
    if (isPageLoading) {
      // wait until page is loaded to prevent state race condition in folder reducer
      return;
    }
    getChartPreference().then((chartPreference) => {
      const {
        showChart,
      } = chartPreference || {};
      setChartShown(!!showChart);
    });
  }, [getChartPreference, isPageLoading]);

  useEffect(() => {
    if (navigation) {
      navigation.updateAssetDetailsParams({ assets });
    }
  }, [assets, navigation]);

  return (
    <div className={classnames('ads-studio-performance-page', classes.assetLibraryPerformancePageContainer)} >
      <div className={classes.subMenu}>
        <span className={classes.title} role="heading" aria-level="1">{i18n.getString('Performance')}</span>
        {renderDatePicker()}
      </div>
      <div className={classnames('asset-library-performance-inline-download')}>
        { isDownloadShown && renderInlineDownload && renderInlineDownload({
          setDownloadShown, totalAssetCount, getInlineDownloadColumns, getInlineDownloadFilter,
        })}
      </div>
      <div className={classnames('asset-library-performance-inline-chart', chartClasses.inlineChartContainer)}>
        { shouldShowChart && renderInlineChart && renderInlineChart({
          isChartShown, perfTimeSeriesData, loadPerfTimeSeriesData, perfMetricDateRange, filters, perfMarker: performancePagePerfMarker,
        })}
      </div>
      <div className="asset-library-performance-grid-container">
        {pagedItems && (!dataOwnerPageName || dataOwnerPageName === AdsStudioPageName.PerfPage) &&
          renderDetailsList({
            pagedItems, onSort, toolbar, pager, columns, sortByColumn, expandColumns,
          })
        }
        {/* show loading indicator */}
        {(isPageLoading || loadingMoreData) && (
          <Overlay>
            <AdsStudioSpinner />
          </Overlay>
        )}
        {/* no content */}
        {(!isPageLoading && hasNoData) && (
          <div>{i18n.getString(_TL_("No assets match your search. Either you don't have any assets associated with your account or your assets don't yet have performance metrics. Please try again."))}</div>
        )}
      </div>
    </div>
  );
};
/* istanbul ignore next */
PerformancePageComponent.propTypes = {
  dataService: PropTypes.shape({
    assets: PropTypes.arrayOf(PropTypes.shape({})),
    totalAssetCount: PropTypes.number,
    loadDataInDateRange: PropTypes.func,
    loadDataArgs: PropTypes.func,
    loadMoreDataInDateRange: PropTypes.func,
  }).isRequired,
  navigation: PropTypes.shape({
    gotoItemDetailFromPerfPage: PropTypes.func,
    gotoEditImageFromPerfPage: PropTypes.func,
  }),
  ianaTimeZone: PropTypes.string,
  dateFormat: PropTypes.string,
  perfMetricDateRange: PropTypes.shape({
    compared: PropTypes.shape({}),
  }),
  adsStudioUserPreferences: PropTypes.shape({
    perfTablePageSize: PropTypes.number,
    perfTableSortBy: PropTypes.shape({}),
    setPerfTablePageSize: PropTypes.func,
    setPerfTableSortBy: PropTypes.func,
  }),
  assetLibraryAccountPreferences: PropTypes.shape({}),
  reload: PropTypes.shape({}), // if changed, force to reload
  logger: PropTypes.shape({
    logTrace: PropTypes.func,
    logError: PropTypes.func,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  renderInlineChart: PropTypes.func,
  getChartPreference: PropTypes.func,

  pagePerfMarker: PropTypes.shape({
    willFetchFile: PropTypes.func.isRequired,
    willInit: PropTypes.func.isRequired,
    willFetchData: PropTypes.func.isRequired,
    willRender: PropTypes.func.isRequired,
    done: PropTypes.func.isRequired,
    createChild: PropTypes.func.isRequired,
  }),

  isAdsStudioAIGCMVPEnabled: PropTypes.bool,
  isAIGCRegionRestricted: PropTypes.bool,
};
/* istanbul ignore next */
PerformancePageComponent.defaultProps = {
  navigation: undefined,
  pagePerfMarker: undefined,
  ianaTimeZone: 'GMT',
  dateFormat: 'M/d/yyyy',
  perfMetricDateRange: undefined,
  adsStudioUserPreferences: undefined,
  assetLibraryAccountPreferences: undefined,
  reload: null,
  renderInlineChart: undefined,
  getChartPreference: () => Promise.resolve({}),

  isAdsStudioAIGCMVPEnabled: false,
  isAIGCRegionRestricted: false,
};
